import { useState, useRef, useCallback, useEffect } from "react";
import { X, Video, Zap } from "lucide-react";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { Portal } from "../portal";
import { cn } from "@/lib/utils";

type AuthModalVariant = "generate" | "feature";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Variant determines default title/description. Use 'generate' for generation flows, 'feature' for locked features */
  variant?: AuthModalVariant;
  /** Optional custom title - overrides variant default */
  title?: string;
  /** Optional custom description - overrides variant default */
  description?: string;
}

const VARIANT_DEFAULTS: Record<
  AuthModalVariant,
  { title: string; description: string; icon: typeof Video }
> = {
  generate: {
    title: "Sign Up to Start Generation",
    description:
      "Create a free account to generate videos and access all your creations.",
    icon: Video,
  },
  feature: {
    title: "Sign Up to Continue",
    description:
      "Create a free account to access this feature and start creating.",
    icon: Zap,
  },
};

const DISMISS_THRESHOLD = 100; // pixels to drag before dismissing

export function AuthModal({
  isOpen,
  onClose,
  variant = "generate",
  title,
  description,
}: AuthModalProps) {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset drag state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Touch handlers for swipe to dismiss (mobile only)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    const isHandle = target.closest("[data-handle]");
    const isScrolledToTop = contentRef.current
      ? contentRef.current.scrollTop === 0
      : true;

    if (isHandle || isScrolledToTop) {
      dragStartY.current = e.touches[0].clientY;
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - dragStartY.current;

      // Only allow dragging down (positive diff)
      if (diff > 0) {
        setDragY(diff);
      }
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    if (dragY > DISMISS_THRESHOLD) {
      onClose();
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY, onClose]);

  if (!isOpen) return null;

  const defaults = VARIANT_DEFAULTS[variant];
  const displayTitle = title || defaults.title;
  const displayDescription = description || defaults.description;
  const Icon = defaults.icon;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm transition-opacity duration-200"
        style={{ opacity: Math.max(0, 1 - dragY / 300) }}
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none overflow-y-auto">
        <div
          ref={contentRef}
          className={cn(
            "relative w-full max-w-md mb-0 mt-auto sm:my-8 bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-white/[0.1] rounded-t-2xl sm:rounded-2xl shadow-2xl pointer-events-auto overflow-hidden max-h-[90vh] sm:max-h-none",
            "will-change-transform touch-none sm:touch-auto",
            !isDragging && "transition-transform duration-300 ease-out"
          )}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Animated gradient accent - shown on all devices */}
          <div
            className="absolute top-0 left-0 right-0 h-1 animate-gradient-x"
            style={{
              background:
                "linear-gradient(90deg, #8b5cf6, #f59e0b, #8b5cf6, #f59e0b)",
              backgroundSize: "300% 100%",
            }}
          />

          {/* Mobile drag handle */}
          <div
            data-handle
            className="flex justify-center pt-3 pb-1 sm:hidden cursor-grab active:cursor-grabbing"
          >
            <div className="w-10 h-1 rounded-full bg-white/20" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 rounded-lg text-[#666] hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <X className="size-5" />
          </button>

          {/* Content */}
          <div className="px-6 pt-8 pb-8 text-center">
            {/* Icon */}
            <div className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-violet-500/20 to-amber-500/20 border border-white/[0.1] mb-5">
              <Icon className="size-7 text-violet-400" />
            </div>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
              {displayTitle}
            </h2>

            {/* Description */}
            <p className="text-[#888] mb-8 max-w-sm mx-auto">
              {displayDescription}
            </p>

            {/* Auth buttons */}
            <div className="flex flex-col gap-3">
              <SignUpButton mode="modal">
                <button className="w-full px-6 py-3.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-400 hover:to-purple-400 text-white font-semibold transition-all">
                  Sign Up Free
                </button>
              </SignUpButton>

              <SignInButton mode="modal">
                <button className="w-full px-6 py-3.5 rounded-xl bg-white/[0.05] border border-white/[0.1] hover:bg-white/[0.08] hover:border-white/[0.15] text-white font-semibold transition-all">
                  Log In
                </button>
              </SignInButton>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
